import React, { useState, useRef, useEffect } from "react";
import Cookies from "js-cookie";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

import { logout } from "../../store/authSlice";
import { apiSlice } from "../../services/apiSlice";
import { useUserProfileQuery } from "../../services/authApiSlice";
import logo from "../../assets/logoWithName.svg";

import classes from "./sideBar.module.css";
import Dropdown from "./dropDown/DropDown";
import { dropdowns, withoutDropdowns } from "./dropDown";
import { XCircleIcon } from "@heroicons/react/24/outline";

const contacts = [
  { id: "phone", link: "+91 9999123511" },
  { id: "email", link: "contact@mobigarage.com" },
];

export const SideBar = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data } = useUserProfileQuery();

  const [activeMenu, setActiveMenu] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const menuRefs = useRef({});

  const handleLogOut = () => {
    Cookies.remove("token");
    Cookies.remove("expirationTime");
    dispatch(logout());
    dispatch(apiSlice.util.resetApiState());
    toast.success("Logged out successfully");
    navigate("login");
    onClose();
  };

  const activeDropdown = dropdowns.find((d) => d.id === activeMenu);

  // calculate submenu position
  useEffect(() => {
    if (activeMenu && menuRefs.current[activeMenu]) {
      const rect = menuRefs.current[activeMenu].getBoundingClientRect();
      const gap = 12;
      const submenuWidth = 220;

      let left = rect.right + gap +10
      let top = rect.top;

      // open on left if overflowing right
      if (left + submenuWidth > window.innerWidth) {
        left = rect.left - submenuWidth - gap;
      }

      const submenuHeight = activeDropdown
        ? activeDropdown.options.length * 48
        : 0;

      if (top + submenuHeight > window.innerHeight) {
        top = window.innerHeight - submenuHeight - gap;
      }

      setSubmenuPosition({ top, left });
    }
  }, [activeMenu, isOpen, activeDropdown]);

  return (
    <>
      {/* BACKDROP */}
      {isOpen && (
        <div
          className={classes.backdrop}
          onClick={() => {
            setActiveMenu(null);
            onClose();
          }}
        />
      )}

      {/* SIDEBAR */}
      <motion.aside
        initial={{ x: "-100%" }}
        animate={{ x: isOpen ? 0 : "-100%" }}
        transition={{ type: "tween", duration: 0.3 }}
        className={classes.stack}
      >
        <div className={classes.container}>
          {/* SCROLLABLE LINKS */}

          <div className={classes.container__header}>
            <img
              src={logo}
              alt="Logo"
              className={classes.container__header__img}
            />
            <button
              className={classes.container__header__closeBtn}
              onClick={() => {
                setActiveMenu(null);
                onClose();
              }}
              aria-label="Close sidebar"
            >
              <XCircleIcon className={classes.container__header__closeIcon} />
            </button>
          </div>

          <div className={classes.scrollable}>
            {/* Primary Links */}

            <div className={classes.dropdown__menu}>
              {withoutDropdowns.map((option) => (
                <NavLink
                  key={option.id}
                  to={option.path}
                  end
                  className={({ isActive }) =>
                    isActive
                      ? `${classes.dropdown__menu__item} ${classes.dropdown__menu__item__active}`
                      : classes.dropdown__menu__item
                  }
                >
                  <img src={option.image} alt={option.name} />
                  <h5 className={classes.dropdown__menu__item__title}>
                    {option.name}
                  </h5>
                </NavLink>
              ))}
            </div>

            {/* Left Sidebar Dropdowns */}
            <div className={classes.leftMenu}>
              {dropdowns.map((dropdown) => (
                <div
                  key={dropdown.id}
                  ref={(el) => (menuRefs.current[dropdown.id] = el)}
                >
                  <Dropdown
                    id={dropdown.id}
                    title={dropdown.title}
                    activeMenu={activeMenu}
                    setActiveMenu={setActiveMenu}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* BOTTOM AREA */}
          <div className={classes.bottom}>
            {/* <div className={classes.container__box__categories}>
              <h1 className={classes.container__box__categories__title}>
                Contact Us
              </h1>
              <div className={classes.container__box__categories__box}>
                {contacts.map((contact) => (
                  <h5
                    key={contact.id}
                    className={
                      classes.container__box__categories__box__category__name
                    }
                  >
                    {contact.link}
                  </h5>
                ))}
              </div>
              <hr className={classes.box__item__divider} />
            </div> */}

            <button className={classes.box__btn} onClick={handleLogOut}>
              Log Out
            </button>
          </div>
        </div>
      </motion.aside>

      {/* FLOATING SUBMENU ON BACKDROP */}
      {activeDropdown && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2 }}
          className={classes.floatingSubMenu}
          style={{
            top: submenuPosition.top,
            left: submenuPosition.left,
          }}
        >
          {activeDropdown.options.map((option) => (
            <NavLink
              key={option.id}
              to={option.path}
              className={classes.subMenuItem}
              onClick={() => {
                setActiveMenu(null);
                onClose();
              }}
            >
              <img src={option.image} alt={option.name} />
              <span className={classes.subMenuItem__title}>{option.name}</span>
            </NavLink>
          ))}
        </motion.div>
      )}
    </>
  );
};
